import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

const WAITS = ["gate_paused", "budget_used_up", "paid_disabled", "gate_unavailable"];

// What a "Retry now" press did, in plain words: never a silent no-op.
export const retryText = (r, duckName) => {
  if (r.outcome === "armed") return `Probe armed for ${duckName || r.creation_id}: its next AI call checks the service now.`;
  if (WAITS.includes(r.code)) return `Probe waiting: ${r.reason}`;
  if (r.code === "cooldown") return `Retry available in ${r.retry_in} s (one probe per minute).`;
  return r.reason ? `${r.reason.charAt(0).toUpperCase()}${r.reason.slice(1)}.` : "Nothing was sent.";
};

const secondsLeft = (iso, now) => (iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 1000)) : 0);

// Owner-only "Retry now": arms a probe for the cheapest parked duck right away (server: spend gate respected, 1/min).
export const ProbeRetry = ({ aiLimit, viewer, onRetry }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [now, setNow] = useState(Date.now());
  const wait = secondsLeft(aiLimit?.retry_available_at, now);
  useEffect(() => {
    if (!wait) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [wait]);

  if (!viewer?.can_raise_budget) {
    return (
      <p data-testid="ai-probe-retry-owner-only" className="mt-3 text-xs text-muted-foreground">
        Only an owner account can retry the probe now{viewer?.why ? ` (${viewer.why})` : ""}.
      </p>
    );
  }
  const retry = async () => {
    setBusy(true);
    try {
      setResult(await onRetry());
    } catch (e) {
      setResult({ outcome: "error", reason: e?.response?.data?.detail || "could not reach the server, please retry" });
    } finally {
      setBusy(false);
      setNow(Date.now());
    }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <Button
        data-testid="ai-probe-retry-btn"
        variant="outline"
        size="sm"
        className="rounded-full h-9 px-4 font-bold gap-2 transition-transform active:scale-95"
        disabled={busy || wait > 0}
        onClick={retry}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        {wait > 0 ? `Retry in ${wait} s` : "Retry now"}
      </Button>
      {result && (
        <span data-testid="ai-probe-retry-result" role="status" className="text-sm font-semibold">
          {retryText(result, aiLimit?.probe_duck_name)}
        </span>
      )}
    </div>
  );
};
